import React, { forwardRef } from 'react';

import type { ButtonProps as MuiButtonProps } from '@mui/material/Button';
import MuiButton from '@mui/material/Button';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';

import { LoadingSpinner } from '../../layouts';

export interface ButtonProps extends Omit<MuiButtonProps, 'variant'> {
  className?: string;
  endIcon?: JSX.Element;
  rel?: string;
  size?: 'small' | 'medium' | 'large';
  startIcon?: JSX.Element;
  status?: 'default' | 'disabled' | 'loading';
  target?: string;
  variant?: 'primary' | 'secondary' | 'tertiary' | 'text';
}

const useStyles = makeStyles((theme: Theme) => ({
  tertiary: {
    background: 'none',
    border: '1px solid transparent',
    color: theme.palette.primary.main,

    '&.Mui-disabled': {
      border: '1px solid transparent',
    },

    '&:hover': {
      background: 'none',
      textDecoration: `underline ${theme.palette.primary.main}`,
      textUnderlineOffset: theme.spacing(0.75),
    },
  },

  loadingState: {
    opacity: 0.2,
  },

  loader: {
    left: 'calc(50% - 12)',
    position: 'absolute',
    top: 'calc(50% - 12)',
  },
}));

export const Button = forwardRef((props: ButtonProps, _ref): JSX.Element => {
  const classes = useStyles();
  const {
    children,
    className = '',
    endIcon = null,
    fullWidth = false,
    size = 'medium',
    status = 'default',
    startIcon = null,
    variant = 'primary',
    ...rest
  } = props;

  const getMuiVariant = (fdVariant) => {
    switch (fdVariant) {
      case 'secondary':
        return 'outlined';
      case 'tertiary':
        return 'outlined';
      case 'text':
        return 'text';
      case 'primary':
      default:
        return 'contained';
    }
  };

  const buttonStyles = clsx(
    'fd-button',
    `fd-button__${variant}`,
    variant === 'tertiary' && classes.tertiary,
    className || '',
  );
  const buttonContentStyles = clsx(
    'buttonState',
    status === 'loading' && classes.loadingState,
  );

  return (
    <MuiButton
      className={buttonStyles}
      disabled={status !== 'default'}
      disableRipple={variant === 'text'}
      endIcon={endIcon}
      fullWidth={fullWidth}
      size={size}
      startIcon={startIcon}
      variant={getMuiVariant(variant)}
      {...rest}
    >
      <div className={buttonContentStyles}>{children}</div>

      {status === 'loading' && (
        <LoadingSpinner className={classes.loader} variant="inline" />
      )}
    </MuiButton>
  );
});

Button.displayName = 'Button';
